import { apexUrl, type CrossHostStageOptions } from '@/lib/cross-host-urls';

/**
 * Apex help / legal destinations shared by the app-surface menu (WEB-628)
 * and the apex/studio {@link HamburgerDrawer}. Kept in one module so the two
 * surfaces cannot drift on path or label.
 *
 * Paths must resolve — never bare paths that 404. Privacy + Terms live under
 * `/legal/*`; `/support` redirects to `/contact` until a dedicated page ships.
 */
export interface HelpLegalPath {
  readonly label: string;
  readonly path: string;
}

export const HELP_LEGAL_PATHS = [
  { label: 'Help', path: '/help' },
  // WEB-1241: the FAQ now lives at /help#faq; link straight to it so menus
  // don't bounce through the /faq -> /help 308.
  { label: 'FAQ', path: '/help#faq' },
  { label: 'Support', path: '/support' },
  { label: 'Contact', path: '/contact' },
  { label: 'About', path: '/about' },
  { label: 'Privacy', path: '/legal/privacy' },
  { label: 'Terms', path: '/legal/terms' },
] as const satisfies readonly HelpLegalPath[];

export interface HelpLegalLink {
  readonly label: string;
  readonly href: string;
}

/**
 * Resolve apex absolute hrefs at call time so staging / env stay live.
 *
 * WEB-826: pass the caller's SSR-seeded staging flag. Without it `apexUrl`
 * falls through to `resolveIsStaging()`'s `window.location.hostname` rung — a
 * branch only the browser can reach — so a client component rendering these
 * links emits production apex hrefs on the server and staging ones in the
 * browser. Omitting `stage` keeps the previous env/window resolution, which is
 * correct for server-only and story/test call sites.
 */
export function helpLegalLinks(stage?: CrossHostStageOptions): readonly HelpLegalLink[] {
  return HELP_LEGAL_PATHS.map(({ label, path }) => ({
    label,
    href: apexUrl(path, stage),
  }));
}
